// src/pages/admin/AdminRecovery.tsx
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { GlobalStyles } from "../../styles/globalStyles";
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  FileText,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Calendar,
  Wallet,
  Hash,
  DollarSign,
  Check,
  MessageSquare,
  Mail,
  User,
  Shield,
  Filter,
  BarChart3,
  DownloadCloud,
  Bell,
  Copy,
  Sparkles,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  Edit,
  Users,
  Activity,
} from "lucide-react";
import { recoveryService, transactionService } from "../../services/api";
import toast from "react-hot-toast";
import {
  formatAbbreviatedCurrency,
  formatCrypto,
  formatCurrency,
} from "../../utils/currency";
import RecoveryChat from "../../components/recovery/RecoveryChat";
import { getUnreadChatCount } from "../../redux/slices/recoveryChatSlice";
import LoadingScreen from "../../components/LoadingScreen";

interface CryptoPrice {
  [key: string]: {
    price: number;
    network: string;
    crypto_id: string;
  };
}

const INITIAL_CURRENCIES = {
  BTC: { price: 0, network: "Bitcoin", crypto_id: "bitcoin" },
  ETH: { price: 0, network: "Ethereum", crypto_id: "ethereum" },
  USDT: { price: 0, network: "Ethereum", crypto_id: "tether" },
  BNB: { price: 0, network: "Binance Smart Chain", crypto_id: "binancecoin" },
  SOL: { price: 0, network: "Solana", crypto_id: "solana" },
};

interface RecoveryCase {
  id: number;
  case_number: string;
  user_name: string;
  user_email: string;
  wallet_address: string;
  transaction_id: string;
  amount: number;
  currency: string;
  network: string;
  status: string;
  recovery_fee: number | null;
  success_fee_percentage: number;
  estimated_recovery_amount: number | null;
  recovery_plan: string | null;
  additional_notes: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  assessment_completed_at?: string;
  recovery_started_at?: string;
  recovery_completed_at?: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    verification_level: number;
    wallet_connected: boolean;
  };
  unread_message_count?: number;
}

interface Statistics {
  overview: {
    total_requests: number;
    pending_requests: number;
    in_progress_requests: number;
    completed_requests: number;
    failed_requests: number;
    success_rate: number;
    total_amount_lost: number;
    estimated_recovery_amount: number;
    recovery_rate: number;
  };
  network_statistics: Array<{
    network: string;
    total: number;
    total_amount: number;
  }>;
  status_distribution: Array<{
    status: string;
    count: number;
  }>;
  monthly_trend: Array<{
    month: string;
    total_requests: number;
    total_amount: number;
  }>;
}

// View Details Modal Component
const RecoveryDetailsModal: React.FC<{
  caseItem: RecoveryCase | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate: (id: number, status: string, data?: any) => Promise<void>;
  onUpdateNotes: (id: number, notes: string) => Promise<void>;
  cryptoPrices: CryptoPrice;
}> = ({
  caseItem,
  isOpen,
  onClose,
  onStatusUpdate,
  onUpdateNotes,
  cryptoPrices,
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "notes">("details");
  const [newNotes, setNewNotes] = useState("");
  const [updatingNotes, setUpdatingNotes] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState({
    status: "",
    recovery_fee: "",
    success_fee_percentage: "",
    estimated_recovery_amount: "",
    recovery_plan: "",
    admin_notes: "",
  });

  if (!isOpen || !caseItem) return null;

  const calculateUsdValue = (amount: number, currency: string) => {
    const currentPrice = cryptoPrices[currency]?.price || 0;
    if (currentPrice > 0) {
      return amount * currentPrice;
    } else {
      const defaultRates: Record<string, number> = {
        BTC: 45000,
        ETH: 3000,
        USDT: 1,
        BNB: 400,
        SOL: 100,
      };
      const fallbackRate = defaultRates[currency] || 1;
      return amount * fallbackRate;
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(`Copied ${field} to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  const getExplorerUrl = (network: string, transactionId: string) => {
    const explorers: Record<string, string> = {
      Bitcoin: `https://blockchain.info/tx/${transactionId}`,
      Ethereum: `https://etherscan.io/tx/${transactionId}`,
      "Binance Smart Chain": `https://bscscan.com/tx/${transactionId}`,
      BSC: `https://bscscan.com/tx/${transactionId}`,
      Solana: `https://solscan.io/tx/${transactionId}`,
    };
    return (
      explorers[network] || `https://blockchair.com/search?q=${transactionId}`
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  const handleUpdateNotes = async () => {
    if (!newNotes.trim()) return;
    setUpdatingNotes(true);
    try {
      await onUpdateNotes(caseItem.id, newNotes);
      setNewNotes("");
      toast.success("Notes updated successfully");
    } catch (error) {
      toast.error("Failed to update notes");
    } finally {
      setUpdatingNotes(false);
    }
  };

  const handleStatusSubmit = async () => {
    try {
      await onStatusUpdate(caseItem.id, statusUpdateData.status, {
        recovery_fee: statusUpdateData.recovery_fee
          ? parseFloat(statusUpdateData.recovery_fee)
          : null,
        success_fee_percentage: statusUpdateData.success_fee_percentage
          ? parseFloat(statusUpdateData.success_fee_percentage)
          : null,
        estimated_recovery_amount: statusUpdateData.estimated_recovery_amount
          ? parseFloat(statusUpdateData.estimated_recovery_amount)
          : null,
        recovery_plan: statusUpdateData.recovery_plan || null,
        admin_notes: statusUpdateData.admin_notes || null,
      });
      setShowStatusUpdate(false);
      setStatusUpdateData({
        status: "",
        recovery_fee: "",
        success_fee_percentage: "",
        estimated_recovery_amount: "",
        recovery_plan: "",
        admin_notes: "",
      });
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-emerald-500 bg-emerald-500/10";
      case "in_progress":
      case "under_review":
      case "assessment_complete":
      case "recovery_plan_sent":
      case "recovery_approved":
        return "text-blue-500 bg-blue-500/10";
      case "failed":
        return "text-rose-500 bg-rose-500/10";
      case "cancelled":
        return "text-gray-500 bg-gray-500/10";
      default:
        return "text-amber-500 bg-amber-500/10";
    }
  };

  const formatStatus = (status: string): string => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 w-full max-w-6xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/50 dark:border-gray-800/50">
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${getStatusColor(caseItem.status)}`}
              >
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recovery Case: {caseItem.case_number}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(caseItem.status)}`}
                  >
                    {formatStatus(caseItem.status)}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    • Submitted {formatDate(caseItem.created_at)}
                  </span>
                  {caseItem.unread_message_count &&
                    caseItem.unread_message_count > 0 && (
                      <span className="flex items-center gap-1 bg-rose-500 text-white text-xs px-2 py-1 rounded-full border border-white/20">
                        <Bell className="h-3 w-3" />
                        {caseItem.unread_message_count} new message
                        {caseItem.unread_message_count !== 1 ? "s" : ""}
                      </span>
                    )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowStatusUpdate(true)}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/update`}
              >
                <Edit className="h-4 w-4 mr-2 transition-transform group-hover/update:-translate-y-0.5" />
                Update Status
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="border-b border-gray-200/50 dark:border-gray-800/50">
            <div className="px-6 flex space-x-4">
              <button
                onClick={() => setActiveTab("details")}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "details"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                Case Details
              </button>
              <button
                onClick={() => setActiveTab("notes")}
                className={`py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "notes"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                Admin Notes
              </button>
            </div>
          </div>

          <div
            className="overflow-y-auto p-6"
            style={{ maxHeight: "calc(90vh - 200px)" }}
          >
            {activeTab === "details" && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className={`${GlobalStyles.card}`}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Case Information
                    </h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Case Number
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.case_number}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Status
                          </p>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(caseItem.status)}`}
                          >
                            {formatStatus(caseItem.status)}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Network
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.network}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Currency
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.currency}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Submission Date
                        </p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formatDate(caseItem.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`${GlobalStyles.card}`}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      User Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          User Name
                        </p>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {caseItem.user_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Email
                        </p>
                        <div className="flex items-center justify-between group">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.user_email}
                          </p>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() =>
                                handleCopy(caseItem.user_email, "Email")
                              }
                              title="Copy email"
                            >
                              {copiedField === "Email" ? (
                                <Check className="h-4 w-4 text-emerald-500" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                            <a
                              href={`mailto:${caseItem.user_email}`}
                              className="text-blue-500 hover:text-blue-600"
                              title="Send email"
                            >
                              <Mail className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`${GlobalStyles.card}`}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <Wallet className="h-4 w-4 mr-2" />
                      Wallet Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Wallet Address
                        </p>
                        <div className="flex items-center justify-between group">
                          <p className="font-mono text-sm truncate max-w-[80%] text-gray-900 dark:text-white">
                            {caseItem.wallet_address}
                          </p>
                          <button
                            onClick={() =>
                              handleCopy(
                                caseItem.wallet_address,
                                "Wallet Address",
                              )
                            }
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Copy address"
                          >
                            {copiedField === "Wallet Address" ? (
                              <Check className="h-4 w-4 text-emerald-500" />
                            ) : (
                              <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className={`${GlobalStyles.card}`}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <Hash className="h-4 w-4 mr-2" />
                      Transaction Details
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Transaction ID
                        </p>
                        <div className="flex items-center justify-between group">
                          <p className="font-mono text-sm truncate max-w-[70%] text-gray-900 dark:text-white">
                            {caseItem.transaction_id}
                          </p>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() =>
                                handleCopy(
                                  caseItem.transaction_id,
                                  "Transaction ID",
                                )
                              }
                              title="Copy transaction ID"
                            >
                              {copiedField === "Transaction ID" ? (
                                <Check className="h-4 w-4 text-emerald-500" />
                              ) : (
                                <Copy className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                            <a
                              href={getExplorerUrl(
                                caseItem.network,
                                caseItem.transaction_id,
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:text-primary/80"
                              title="View on explorer"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Amount Lost
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatCrypto(caseItem.amount, caseItem.currency)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            USD Value
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            ~$
                            {formatCurrency(
                              calculateUsdValue(
                                caseItem.amount,
                                caseItem.currency,
                              ),
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`${GlobalStyles.card}`}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Recovery Information
                    </h4>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Estimated Recovery
                          </p>
                          <p className="font-medium text-emerald-500">
                            {caseItem.estimated_recovery_amount
                              ? formatCrypto(
                                  caseItem.estimated_recovery_amount,
                                  caseItem.currency,
                                )
                              : "—"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Success Fee
                          </p>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {caseItem.success_fee_percentage > 0
                              ? `${caseItem.success_fee_percentage}%`
                              : "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`${GlobalStyles.card}`}>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      Recovery Timeline
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                          Submitted:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatDate(caseItem.created_at)}
                        </span>
                      </div>
                      {caseItem.assessment_completed_at && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-400">
                            Assessment Completed:
                          </span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatDate(caseItem.assessment_completed_at)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400">
                          Last Updated:
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatDate(caseItem.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "notes" && (
              <div className="space-y-6">
                <div className={`${GlobalStyles.card}`}>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                    Current Admin Notes
                  </h4>
                  {caseItem.admin_notes ? (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {caseItem.admin_notes}
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 italic">
                      No admin notes yet.
                    </p>
                  )}
                </div>

                <div className={`${GlobalStyles.card}`}>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                    Add/Update Notes
                  </h4>
                  <textarea
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    placeholder="Add internal notes about this case..."
                    className={`${GlobalStyles.input} min-h-[150px] resize-none`}
                  />
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleUpdateNotes}
                      disabled={!newNotes.trim() || updatingNotes}
                      className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/update ${
                        !newNotes.trim() || updatingNotes
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {updatingNotes ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <div className="flex items-center">
                          <Edit className="h-4 w-4 mr-2 transition-transform group-hover/update:-translate-x-0.5" />
                          Update Notes
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {showStatusUpdate && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 w-full max-w-md">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Update Case Status
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label
                        className={`${GlobalStyles.inputLabel} block mb-2`}
                      >
                        Status
                      </label>
                      <select
                        value={statusUpdateData.status}
                        onChange={(e) =>
                          setStatusUpdateData({
                            ...statusUpdateData,
                            status: e.target.value,
                          })
                        }
                        className={`${GlobalStyles.input}`}
                      >
                        <option value="">Select Status</option>
                        <option value="under_review">Under Review</option>
                        <option value="assessment_complete">
                          Assessment Complete
                        </option>
                        <option value="recovery_plan_sent">
                          Recovery Plan Sent
                        </option>
                        <option value="recovery_approved">
                          Recovery Approved
                        </option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    {statusUpdateData.status === "assessment_complete" && (
                      <>
                        <div>
                          <label
                            className={`${GlobalStyles.inputLabel} block mb-2`}
                          >
                            Estimated Recovery Amount
                          </label>
                          <input
                            type="number"
                            step="0.00000001"
                            value={statusUpdateData.estimated_recovery_amount}
                            onChange={(e) =>
                              setStatusUpdateData({
                                ...statusUpdateData,
                                estimated_recovery_amount: e.target.value,
                              })
                            }
                            className={`${GlobalStyles.input}`}
                            placeholder={`Amount in ${caseItem.currency}`}
                          />
                        </div>
                        <div>
                          <label
                            className={`${GlobalStyles.inputLabel} block mb-2`}
                          >
                            Success Fee Percentage
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            max="100"
                            value={statusUpdateData.success_fee_percentage}
                            onChange={(e) =>
                              setStatusUpdateData({
                                ...statusUpdateData,
                                success_fee_percentage: e.target.value,
                              })
                            }
                            className={`${GlobalStyles.input}`}
                            placeholder="20"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label
                        className={`${GlobalStyles.inputLabel} block mb-2`}
                      >
                        Recovery Plan (Optional)
                      </label>
                      <textarea
                        value={statusUpdateData.recovery_plan}
                        onChange={(e) =>
                          setStatusUpdateData({
                            ...statusUpdateData,
                            recovery_plan: e.target.value,
                          })
                        }
                        className={`${GlobalStyles.input} min-h-[100px] resize-none`}
                        placeholder="Enter recovery plan details..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowStatusUpdate(false)}
                      className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleStatusSubmit}
                      disabled={!statusUpdateData.status}
                      className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} group/submit ${
                        !statusUpdateData.status
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {!statusUpdateData.status ? (
                        "Select Status"
                      ) : (
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 transition-transform group-hover/submit:-translate-x-0.5" />
                          Update Status
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Statistics Modal
const StatisticsModal: React.FC<{
  statistics: Statistics | null;
  isOpen: boolean;
  onClose: () => void;
  cryptoPrices: CryptoPrice;
}> = ({ statistics, isOpen, onClose, cryptoPrices }) => {
  if (!isOpen || !statistics) return null;

  const calculateUsdValue = (amount: number, currency: string) => {
    const currentPrice = cryptoPrices[currency]?.price || 0;
    if (currentPrice > 0) {
      return amount * currentPrice;
    } else {
      const defaultRates: Record<string, number> = {
        BTC: 45000,
        ETH: 3000,
        USDT: 1,
        BNB: 400,
        SOL: 100,
      };
      const fallbackRate = defaultRates[currency] || 1;
      return amount * fallbackRate;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200/50 dark:border-gray-800/50">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-primary" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Recovery Statistics
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div
            className="overflow-y-auto p-6"
            style={{ maxHeight: "calc(90vh - 120px)" }}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className={`${GlobalStyles.card} !p-5`}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Cases
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {statistics.overview.total_requests}
                </p>
              </div>
              <div className={`${GlobalStyles.card} !p-5`}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Success Rate
                </p>
                <p className="text-2xl font-bold text-emerald-500 mt-1">
                  {statistics.overview.success_rate}%
                </p>
              </div>
              <div className={`${GlobalStyles.card} !p-5`}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total Amount Lost
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  $
                  {formatCurrency(
                    calculateUsdValue(
                      statistics.overview.total_amount_lost,
                      "BTC",
                    ),
                  )}
                </p>
              </div>
              <div className={`${GlobalStyles.card} !p-5`}>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Recovery Rate
                </p>
                <p className="text-2xl font-bold text-emerald-500 mt-1">
                  {statistics.overview.recovery_rate}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminRecovery: React.FC = () => {
  const dispatch = useDispatch();
  const [recoveryCases, setRecoveryCases] = useState<RecoveryCase[]>([]);
  const [filter, setFilter] = useState({
    status: "",
    network: "",
    search: "",
    date_from: "",
    date_to: "",
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });
  const [selectedCase, setSelectedCase] = useState<RecoveryCase | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [updatingCaseId, setUpdatingCaseId] = useState<number | null>(null);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [cryptoPrices, setCryptoPrices] = useState<CryptoPrice>(() => {
    const savedPrices = localStorage.getItem("obelisk_crypto_prices");
    if (savedPrices) {
      try {
        const parsed = JSON.parse(savedPrices);
        return { ...INITIAL_CURRENCIES, ...parsed };
      } catch (e) {
        return INITIAL_CURRENCIES;
      }
    }
    return INITIAL_CURRENCIES;
  });
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>(() => {
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    return savedUpdate || "";
  });
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [showChatModal, setShowChatModal] = useState<{
    isOpen: boolean;
    caseId: number;
    caseNumber: string;
  }>({
    isOpen: false,
    caseId: 0,
    caseNumber: "",
  });

  useEffect(() => {
    fetchRecoveryCases();
    fetchStatistics();
    fetchCryptoPrices();
    startPolling();

    const priceInterval = setInterval(fetchCryptoPrices, 60000);

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      clearInterval(priceInterval);
    };
  }, [
    filter.status,
    filter.network,
    pagination.current_page,
    filter.date_from,
    filter.date_to,
  ]);

  const savePricesToStorage = (prices: CryptoPrice, timestamp: number) => {
    try {
      localStorage.setItem("obelisk_crypto_prices", JSON.stringify(prices));
      localStorage.setItem(
        "obelisk_prices_last_update_timestamp",
        timestamp.toString(),
      );
      const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
      setLastUpdate(updateTime);
      localStorage.setItem("obelisk_prices_last_update", updateTime);
    } catch (error) {
      console.error("Error saving prices to storage:", error);
    }
  };

  const fetchCryptoPrices = async () => {
    setFetchingPrices(true);
    try {
      const response = await transactionService.getCryptoPrices();
      if (response.success && response.prices) {
        const mergedPrices = { ...cryptoPrices };
        let hasNewData = false;

        Object.entries(response.prices).forEach(([currency, data]) => {
          const priceData = data as any;
          if (
            priceData &&
            typeof priceData === "object" &&
            typeof priceData.price === "number" &&
            priceData.price > 0
          ) {
            mergedPrices[currency] = priceData;
            hasNewData = true;
          }
        });

        if (hasNewData) {
          setCryptoPrices(mergedPrices);
          savePricesToStorage(
            mergedPrices,
            response.timestamp || Math.floor(Date.now() / 1000),
          );
        }
      }
    } catch (error: any) {
      console.error("Price fetch error:", error);
      const savedTimestamp = localStorage.getItem(
        "obelisk_prices_last_update_timestamp",
      );
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const updateTime = new Date(timestamp * 1000).toLocaleTimeString();
        setLastUpdate(updateTime);
        toast.error("Network error - Using cached prices");
      }
    } finally {
      setFetchingPrices(false);
    }
  };

  const calculateUsdValue = (amount: number, currency: string) => {
    const currentPrice = cryptoPrices[currency]?.price || 0;
    if (currentPrice > 0) {
      return amount * currentPrice;
    } else {
      const defaultRates: Record<string, number> = {
        BTC: 45000,
        ETH: 3000,
        USDT: 1,
        BNB: 400,
        SOL: 100,
      };
      const fallbackRate = defaultRates[currency] || 1;
      return amount * fallbackRate;
    }
  };

  const startPolling = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    const interval = setInterval(() => {
      checkForNewMessages();
    }, 30000);
    setRefreshInterval(interval);
  };

  const checkForNewMessages = async () => {
    try {
      await dispatch(getUnreadChatCount() as any);
      fetchRecoveryCases();
    } catch (error) {
      console.error("Error checking for new messages:", error);
    }
  };

  const fetchRecoveryCases = async () => {
    setLoading(true);
    try {
      const response = await recoveryService.getRecoveryRequests({
        status: filter.status || undefined,
        network: filter.network || undefined,
        search: filter.search || undefined,
        date_from: filter.date_from || undefined,
        date_to: filter.date_to || undefined,
        page: pagination.current_page,
        per_page: pagination.per_page,
      });

      let apiData;
      if (response.data) {
        apiData = response;
      } else if (Array.isArray(response)) {
        apiData = {
          data: response,
          current_page: 1,
          last_page: 1,
          per_page: response.length,
          total: response.length,
          from: 1,
          to: response.length,
        };
      } else {
        apiData = response;
      }

      const transformedCases = (apiData.data || []).map((item: any) => {
        let userName = "Unknown User";
        let userEmail = "N/A";

        if (item.user) {
          userName = `${item.user.first_name || ""} ${
            item.user.last_name || ""
          }`.trim();
          userEmail = item.user.email || "N/A";
        } else if (item.user_name) {
          userName = item.user_name;
        } else if (item.user_email) {
          userEmail = item.user_email;
        }

        const unreadCount = item.unread_message_count || 0;

        return {
          id: item.id,
          case_number: item.case_number,
          user_name: userName || `User #${item.user_id}`,
          user_email: userEmail || `user${item.user_id}@example.com`,
          wallet_address: item.wallet_address || "",
          transaction_id: item.transaction_id || "",
          amount: parseFloat(item.amount) || 0,
          currency: item.currency || "BTC",
          network: item.network || "Unknown",
          status: item.status || "submitted",
          recovery_fee: item.recovery_fee
            ? parseFloat(item.recovery_fee)
            : null,
          success_fee_percentage: item.success_fee_percentage
            ? parseFloat(item.success_fee_percentage)
            : 0,
          estimated_recovery_amount: item.estimated_recovery_amount
            ? parseFloat(item.estimated_recovery_amount)
            : null,
          recovery_plan: item.recovery_plan || null,
          additional_notes: item.additional_notes || null,
          admin_notes: item.admin_notes || null,
          created_at: item.created_at || new Date().toISOString(),
          updated_at: item.updated_at || new Date().toISOString(),
          assessment_completed_at: item.assessment_completed_at,
          recovery_started_at: item.recovery_started_at,
          recovery_completed_at: item.recovery_completed_at,
          user: item.user,
          unread_message_count: unreadCount,
        };
      });

      setRecoveryCases(transformedCases);
      setPagination({
        current_page: apiData.current_page || 1,
        last_page: apiData.last_page || 1,
        per_page: apiData.per_page || 20,
        total: apiData.total || 0,
      });
    } catch (error: any) {
      console.error("Error fetching recovery cases:", error);
      toast.error(
        error.response?.data?.error || "Failed to load recovery cases",
      );
      setRecoveryCases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await recoveryService.getRecoveryStatistics();
      setStatistics(response);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  const handleStatusUpdate = async (id: number, status: string, data?: any) => {
    setUpdatingCaseId(id);
    try {
      await recoveryService.updateRecoveryStatus(id, { status, ...data });
      toast.success(`Recovery case status updated to ${formatStatus(status)}`);
      fetchRecoveryCases();
      if (selectedCase?.id === id) {
        setSelectedCase(null);
        setDetailsModalOpen(false);
      }
    } catch (error: any) {
      console.error("Status update error:", error);
      toast.error(
        error.response?.data?.error || "Failed to update recovery status",
      );
    } finally {
      setUpdatingCaseId(null);
    }
  };

  const handleUpdateNotes = async (id: number, notes: string) => {
    try {
      await recoveryService.addRecoveryNotes(id, notes);
      fetchRecoveryCases();
    } catch (error: any) {
      console.error("Update notes error:", error);
      throw error;
    }
  };

  const handleExportReport = async () => {
    setExporting(true);
    try {
      const startDate =
        filter.date_from ||
        new Date(new Date().setMonth(new Date().getMonth() - 1))
          .toISOString()
          .split("T")[0];
      const endDate = filter.date_to || new Date().toISOString().split("T")[0];

      const response = await recoveryService.downloadRecoveryReport({
        start_date: startDate,
        end_date: endDate,
        format: "csv",
      });

      if (response.data) {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `recovery_report_${startDate}_to_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const dataStr = JSON.stringify(response, null, 2);
        const dataBlob = new Blob([dataStr], { type: "application/json" });
        const url = window.URL.createObjectURL(dataBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `recovery_report_${startDate}_to_${endDate}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      toast.success("Report exported successfully");
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        error.response?.data?.error ||
          error.message ||
          "Failed to export report",
      );
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.last_page) {
      setPagination((prev) => ({ ...prev, current_page: page }));
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case "in_progress":
        return <Clock className="h-4 w-4 text-blue-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-rose-500" />;
      case "cancelled":
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800";
      case "in_progress":
      case "under_review":
      case "assessment_complete":
      case "recovery_plan_sent":
      case "recovery_approved":
        return "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800";
      case "failed":
        return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800";
      case "cancelled":
        return "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700";
      default:
        return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800";
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const openDetailsModal = (caseItem: RecoveryCase) => {
    setSelectedCase(caseItem);
    setDetailsModalOpen(true);
  };

  const clearFilters = () => {
    setFilter({
      status: "",
      network: "",
      search: "",
      date_from: "",
      date_to: "",
    });
  };

  const handleChatOpen = (caseItem: RecoveryCase) => {
    setShowChatModal({
      isOpen: true,
      caseId: caseItem.id,
      caseNumber: caseItem.case_number,
    });
  };

  const handleRefresh = async () => {
    await fetchRecoveryCases();
    await fetchCryptoPrices();
    await dispatch(getUnreadChatCount() as any);
    toast.success("Refreshed recovery cases and prices");
  };

  const getLastUpdateDisplay = () => {
    if (fetchingPrices) return "Updating...";
    if (lastUpdate) return `Last updated: ${lastUpdate}`;
    const savedUpdate = localStorage.getItem("obelisk_prices_last_update");
    if (savedUpdate) {
      setLastUpdate(savedUpdate);
      return `Last updated: ${savedUpdate}`;
    }
    return "Never updated";
  };

  const totalUnreadMessages = recoveryCases.reduce(
    (total, caseItem) => total + (caseItem.unread_message_count || 0),
    0,
  );

  const casesWithUnreadMessages = recoveryCases.filter(
    (caseItem) => (caseItem.unread_message_count || 0) > 0,
  ).length;

  const totalCases = recoveryCases.length;
  const totalInProgress = recoveryCases.filter(
    (c) => c.status === "in_progress",
  ).length;
  const totalCompleted = recoveryCases.filter(
    (c) => c.status === "completed",
  ).length;
  const totalValue = recoveryCases.reduce(
    (sum, c) => sum + calculateUsdValue(c.amount, c.currency),
    0,
  );

  if (loading && recoveryCases.length === 0) {
    return <LoadingScreen message="Loading recovery cases..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Recovery Cases
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage and monitor fund recovery cases
                {totalUnreadMessages > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 bg-rose-500 text-white text-xs px-2 py-1 rounded-full border border-white/20">
                    <Bell className="h-3 w-3" />
                    {totalUnreadMessages} unread message
                    {totalUnreadMessages !== 1 ? "s" : ""} across{" "}
                    {casesWithUnreadMessages} case
                    {casesWithUnreadMessages !== 1 ? "s" : ""}
                  </span>
                )}
                <span className="block text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {getLastUpdateDisplay()}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div
          className={`${GlobalStyles.card} group hover:border-primary/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <span
              className={`${GlobalStyles.badge} ${GlobalStyles.badgePrimary}`}
            >
              Cases
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Total Cases
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {totalCases}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {
                recoveryCases.filter((c) =>
                  ["submitted", "under_review", "assessment_complete"].includes(
                    c.status,
                  ),
                ).length
              }{" "}
              pending
            </p>
          </div>
        </div>

        <div
          className={`${GlobalStyles.card} group hover:border-blue-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-400/10">
              <Clock className="h-6 w-6 text-blue-500" />
            </div>
            <span className={`${GlobalStyles.badge} ${GlobalStyles.badgeInfo}`}>
              Progress
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            In Progress
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {totalInProgress}
          </p>
          <div className="mt-4">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-400 to-blue-500"
                style={{ width: `${(totalInProgress / totalCases) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {((totalInProgress / totalCases) * 100).toFixed(1)}% of cases
            </p>
          </div>
        </div>

        <div
          className={`${GlobalStyles.card} group hover:border-rose-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-rose-500/20 to-rose-400/10">
              <Bell className="h-6 w-6 text-rose-500" />
            </div>
            <span
              className={`${GlobalStyles.badge} ${GlobalStyles.badgeError}`}
            >
              Messages
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Unread Messages
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {totalUnreadMessages}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {casesWithUnreadMessages} case
            {casesWithUnreadMessages !== 1 ? "s" : ""} need attention
          </p>
        </div>

        <div
          className={`${GlobalStyles.card} group hover:border-purple-300/30 transition-all duration-300`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-400/10">
              <DollarSign className="h-6 w-6 text-purple-500" />
            </div>
            <span className={`${GlobalStyles.badge} ${GlobalStyles.badgeInfo}`}>
              Value
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Total Value
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {formatAbbreviatedCurrency(totalValue)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Using current prices
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className={`${GlobalStyles.card}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Filters
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center gap-1 text-sm text-primary group/advanced"
            >
              <Filter className="h-4 w-4 transition-transform group-hover/advanced:rotate-180" />
              {showAdvancedFilters ? "Hide Advanced" : "Advanced Filters"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="relative group">
            <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className={`${GlobalStyles.input} pl-10 appearance-none pr-8`}
            >
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="assessment_complete">Assessment Complete</option>
              <option value="recovery_plan_sent">Recovery Plan Sent</option>
              <option value="recovery_approved">Recovery Approved</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative group">
            <Wallet className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
            <select
              value={filter.network}
              onChange={(e) =>
                setFilter({ ...filter, network: e.target.value })
              }
              className={`${GlobalStyles.input} pl-10 appearance-none pr-8`}
            >
              <option value="">All Networks</option>
              <option value="Bitcoin">Bitcoin</option>
              <option value="Ethereum">Ethereum</option>
              <option value="Binance Smart Chain">Binance Smart Chain</option>
              <option value="Solana">Solana</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative group">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
            <input
              type="text"
              placeholder="Search cases..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className={`${GlobalStyles.input} pl-10`}
            />
          </div>
        </div>

        {showAdvancedFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200/50 dark:border-gray-800/50">
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Date From
              </label>
              <input
                type="date"
                value={filter.date_from}
                onChange={(e) =>
                  setFilter({ ...filter, date_from: e.target.value })
                }
                className={`${GlobalStyles.input}`}
              />
            </div>
            <div>
              <label className={`${GlobalStyles.inputLabel} block mb-2`}>
                Date To
              </label>
              <input
                type="date"
                value={filter.date_to}
                onChange={(e) =>
                  setFilter({ ...filter, date_to: e.target.value })
                }
                className={`${GlobalStyles.input}`}
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-gray-200/50 dark:border-gray-800/50 mt-4">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {recoveryCases.length}
              </span>{" "}
              cases match your filters
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowStatistics(true)}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} group/stats`}
            >
              <BarChart3 className="h-4 w-4 mr-2 transition-transform group-hover/stats:-translate-y-0.5" />
              Statistics
            </button>
            <button
              onClick={handleExportReport}
              disabled={exporting}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} group/export disabled:opacity-50`}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <DownloadCloud className="h-4 w-4 mr-2 transition-transform group-hover/export:-translate-y-0.5" />
              )}
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Recovery Cases Table */}
      <div className={`${GlobalStyles.card} !p-0 overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className={GlobalStyles.table}>
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Case #
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Chat
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Network
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
              {recoveryCases.length > 0 ? (
                recoveryCases.map((caseItem) => (
                  <tr
                    key={caseItem.id}
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-all duration-200 group/row"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {caseItem.case_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleChatOpen(caseItem)}
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} flex items-center gap-1 relative group/chat`}
                          title="Open Chat"
                        >
                          <MessageSquare className="h-3 w-3 transition-transform group-hover/chat:-translate-x-0.5" />
                          Chat
                          {caseItem.unread_message_count &&
                            caseItem.unread_message_count > 0 && (
                              <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center border border-white/20">
                                {caseItem.unread_message_count}
                              </span>
                            )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {caseItem.user_name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {caseItem.user_email}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {formatCrypto(caseItem.amount, caseItem.currency)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ~$
                        {formatCurrency(
                          calculateUsdValue(caseItem.amount, caseItem.currency),
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-xs ${GlobalStyles.badge} ${GlobalStyles.badgePrimary}`}
                      >
                        {caseItem.network}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(caseItem.status)}
                        <span
                          className={`${GlobalStyles.badge} ml-2 ${getStatusColor(caseItem.status)}`}
                        >
                          {formatStatus(caseItem.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(caseItem.created_at).toLocaleDateString()}
                      </span>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(caseItem.created_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetailsModal(caseItem)}
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} flex items-center gap-1 group/details`}
                          title="View Details"
                        >
                          <Eye className="h-3 w-3 transition-transform group-hover/details:-translate-x-0.5" />
                          Details
                        </button>
                        <div className="relative">
                          <select
                            value={caseItem.status}
                            onChange={(e) =>
                              handleStatusUpdate(caseItem.id, e.target.value)
                            }
                            disabled={updatingCaseId === caseItem.id}
                            className={`${GlobalStyles.input} text-sm py-1.5 pr-8 pl-3 min-w-[140px] ${
                              updatingCaseId === caseItem.id
                                ? "opacity-70 cursor-wait"
                                : ""
                            }`}
                          >
                            <option value="submitted">Submitted</option>
                            <option value="under_review">Under Review</option>
                            <option value="assessment_complete">
                              Assessment Complete
                            </option>
                            <option value="recovery_plan_sent">
                              Recovery Plan Sent
                            </option>
                            <option value="recovery_approved">
                              Recovery Approved
                            </option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          {updatingCaseId === caseItem.id && (
                            <Loader2 className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 animate-spin text-blue-500" />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-12">
                    <div className="text-center">
                      <div className="relative mx-auto w-20 h-20 mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-full blur-lg"></div>
                        <div className="relative w-20 h-20 bg-white/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200/50 dark:border-gray-800/50">
                          <FileText className="h-8 w-8 text-gray-400" />
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {loading ? "Loading..." : "No recovery cases found"}
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {recoveryCases.length === 0
                          ? "No recovery cases yet. They will appear here when users submit requests."
                          : "Try adjusting your filters"}
                      </p>
                      {recoveryCases.length === 0 && (
                        <button
                          onClick={handleRefresh}
                          className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} mt-4 group/refresh`}
                        >
                          <RefreshCw className="h-4 w-4 mr-2 transition-transform group-hover/refresh:rotate-180" />
                          Refresh
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {recoveryCases.length > 0 && pagination.total > pagination.per_page && (
          <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-200/50 dark:border-gray-800/50 gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {Math.min(
                  (pagination.current_page - 1) * pagination.per_page + 1,
                  pagination.total,
                )}
              </span>{" "}
              to{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total,
                )}
              </span>{" "}
              of{" "}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {pagination.total}
              </span>{" "}
              cases
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 group/prev"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4 transition-transform group-hover/prev:-translate-x-0.5" />
              </button>

              {Array.from(
                { length: Math.min(5, pagination.last_page) },
                (_, i) => {
                  let pageNum: number;
                  if (pagination.last_page <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNum = i + 1;
                  } else if (
                    pagination.current_page >=
                    pagination.last_page - 2
                  ) {
                    pageNum = pagination.last_page - 4 + i;
                  } else {
                    pageNum = pagination.current_page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        pagination.current_page === pageNum
                          ? "bg-gradient-to-r from-primary to-primary/90 text-white shadow-sm"
                          : "bg-white/50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 border border-gray-200/50 dark:border-gray-700/50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                },
              )}

              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 group/next"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4 transition-transform group-hover/next:translate-x-0.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <RecoveryDetailsModal
        caseItem={selectedCase}
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedCase(null);
        }}
        onStatusUpdate={handleStatusUpdate}
        onUpdateNotes={handleUpdateNotes}
        cryptoPrices={cryptoPrices}
      />

      {/* Statistics Modal */}
      <StatisticsModal
        statistics={statistics}
        isOpen={showStatistics}
        onClose={() => setShowStatistics(false)}
        cryptoPrices={cryptoPrices}
      />

      {/* Chat Modal */}
      {showChatModal.isOpen && (
        <RecoveryChat
          recoveryId={showChatModal.caseId}
          caseNumber={showChatModal.caseNumber}
          isAdmin={true}
          onClose={() => {
            setShowChatModal({ isOpen: false, caseId: 0, caseNumber: "" });
            fetchRecoveryCases();
            dispatch(getUnreadChatCount() as any);
          }}
          isOpen={showChatModal.isOpen}
        />
      )}
    </div>
  );
};

export default AdminRecovery;
